'use client'

import { useEffect, useState, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CardWatermark } from '@/components/ui/card-watermark'
import { Skeleton } from '@/components/ui/skeleton'
import { Icons } from '@/components/ui/icons'
import { apiClient } from '@/lib/api-client'
import { appendScopeFilterParams } from '@/lib/dashboard-scope-filters'
import { cn } from '@/lib/utils'

interface FatalRow {
  priority: number
  policy_name: string
  call_count: number
  avg_score: number | null
  pct_of_fatal_calls: number
}

interface FatalPriorityData {
  rows: FatalRow[]
  total_fatal_calls: number
  total_calls: number
  ai_insight: string | null
}

interface Props {
  startDate: string
  endDate: string
  targetEmployeeId?: number
  selectedProduct?: string
  selectedSite?: string
}

function ScoreBadge({ score }: { score: number | null }) {
  if (score === null) return <span className="text-muted-foreground text-xs">N/A</span>
  const color =
    score >= 70 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' :
    score >= 50 ? 'text-amber-700 bg-amber-50 border-amber-200' :
                  'text-red-700 bg-red-50 border-red-200'
  return (
    <span className={cn('px-2 py-0.5 rounded-full text-xs font-semibold border', color)}>
      {score}%
    </span>
  )
}

function PriorityBadge({ priority }: { priority: number }) {
  return (
    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-navy text-white text-xs font-bold shrink-0">
      P{priority}
    </span>
  )
}

export function FatalPriorityTable({ startDate, endDate, targetEmployeeId, selectedProduct, selectedSite }: Props) {
  const [data, setData] = useState<FatalPriorityData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!startDate || !endDate) return
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate })
      if (targetEmployeeId) params.set('target_employee_id', String(targetEmployeeId))
      appendScopeFilterParams(params, selectedProduct, selectedSite)
      const res = await apiClient.get<FatalPriorityData>(`/api/analysis/fatal-priority-table?${params}`)
      setData(res)
    } catch {
      setError('Failed to load fatal priority data')
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate, targetEmployeeId, selectedProduct, selectedSite])

  useEffect(() => { load() }, [load])

  const maxCount = Math.max(...(data?.rows ?? []).map(r => r.call_count), 1)

  return (
    <Card className="relative overflow-hidden">
      <CardWatermark opacity={2} />
      <CardHeader className="relative z-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Icons.alertTriangle className="h-5 w-5 text-red-500" />
              Fatal Priority Impact Table
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Each call counted once under its most critical fatal (P1 = highest) · all fatal parameters shown
            </p>
          </div>
          {data && (
            <div className="shrink-0 text-right">
              <p className="text-2xl font-bold text-brand-navy">{data.total_fatal_calls}</p>
              <p className="text-xs text-muted-foreground">fatal calls</p>
              <p className="text-xs text-muted-foreground">of {data.total_calls} total</p>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="relative z-10">
        {loading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full rounded-lg" />)}
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-40 text-sm text-red-500 gap-2">
            <Icons.alertCircle className="h-4 w-4" />
            {error}
          </div>
        ) : !data || data.rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-muted-foreground gap-2">
            <Icons.activity className="h-8 w-8 opacity-30" />
            <p className="text-sm">No fatal violation data for selected date range</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Table */}
            <div className="overflow-x-auto rounded-xl border border-gray-100">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 w-12">Priority</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Quality Parameter</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600 w-28">Fatal Calls</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600 w-28">Avg Score</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 w-40">Share of Fatal</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, idx) => (
                    <tr
                      key={row.priority}
                      className={cn(
                        'border-b border-gray-50 hover:bg-gray-50/60 transition-colors',
                        idx === 0 && 'bg-red-50/40'
                      )}
                    >
                      <td className="px-4 py-3">
                        <PriorityBadge priority={row.priority} />
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-800">{row.policy_name}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-800">
                        {row.call_count}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <ScoreBadge score={row.avg_score} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                            <div
                              className={cn(
                                'h-2 rounded-full',
                                idx === 0 ? 'bg-red-500' : idx === 1 ? 'bg-orange-400' : 'bg-amber-300'
                              )}
                              style={{ width: `${(row.call_count / maxCount) * 100}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground w-10 text-right">
                            {row.pct_of_fatal_calls}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* AI Insight */}
            {data.ai_insight && (
              <div className="rounded-lg bg-brand-cornflower/5 border border-brand-cornflower/20 p-4">
                <p className="text-xs font-semibold text-brand-cornflower mb-1 uppercase tracking-wide flex items-center gap-1">
                  <Icons.sparkles className="h-3 w-3" />
                  AI Insight
                </p>
                <p className="text-sm text-gray-700 leading-relaxed">{data.ai_insight}</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
